import {Injectable, Injector} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Action, select, Store} from '@ngrx/store';
import {PatientDataState} from '@ispm-client/modules/patient';
import {
  ConfigureProcessExecutorModuleAction,
  CurationInfoForPathwaysActionTypes,
  FetchClinicalTrialsAction,
  FetchPathwayProgressionAction,
  GetCurationItemById,
  HandlePathwaysDataFetchedByKeysAction,
  LoadPathwaysAction,
  LoadPathwaysActionTypes,
  PathwayActionTypes,
  SearchCurationItemByKeysAction,
  UpdateBpmnNodesCurationData, UpdateBpmnNodeSelectionDepdendenciesCurationData,
  UpdateClinicalTrialsAction,
  UpdateProgressionAction
} from '@ispm-client/modules/pathways/store/actions';
import {catchError, filter, map, switchMap, take, withLatestFrom, tap} from 'rxjs/operators';
import {
  BP_Status,
  InitExecutionAction,
  LoadExsitingExecutionAction,
  selectExecutionHistoryParsingStatus,
  UpdateGuidedBoxesAction, UpdateNodesDependenciesAction,
  UpdateNodesNamesAction, UpdateTreeNavigationConfAction, UpdateUserSelectionStatusAction, USER_SELECTION_STATUS
} from '@ispm-client/modules/process-executor/store';
import { Observable } from 'rxjs';
import { Progression } from '@ispm-shared/modules/common';
import { from } from 'rxjs';
import * as _ from 'lodash';
import {PathwaysTriggerOff} from '@ispm/test/store/prefetch-data/prefetch-data.actions';
import {
  selectIsPathwaysSelectionActive,
  selectPathwayTestProgression
} from '@ispm-client/modules/pathways/store/selectors';
import {
  failedToLoadGuideboxesMessage,
  failedToLoadNodesDependenciesMessage,
  PathwaysExecutionData
} from '@ispm-client/modules/pathways/models';
import {of} from 'rxjs/observable/of';
import {SetErrorAction} from '@ispm/shared/store/actions/error.actions';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {PathwaysConfigurationService} from '@ispm-client/modules/pathways/services';
import {LogService} from '@ispm/core/services/log.service';
import {TestBarEnableConfirmButtonAction} from "@ispm/test/store/test-bar.actions";


@Injectable()
export class LoadPathwaysEffects {
  @Effect()
  provideConfigToBpmnModuleEffect$ = this.actions$.pipe(
    ofType<ConfigureProcessExecutorModuleAction>(LoadPathwaysActionTypes.CONFIGURE_PROCESS_EXECUTOR_MODULE),
    tap((action) => this.sendLog('provideConfigToBpmnModuleEffect$', {actionType: action.type})),
    switchMap((action) => {
      return forkJoin(
        Observable.of(action),
        this.getPathwaysConfigurationService().getTreeNavigationPolicy().pipe(take(1)),
      );
    }),
    switchMap(([action, conf]) => [new UpdateTreeNavigationConfAction({treeNavigationConf: conf})])
  );

  @Effect()
  startLoadingPathwayEffect$ = this.actions$.pipe(
    ofType<LoadPathwaysAction>(CurationInfoForPathwaysActionTypes.LOAD_PATHWAYS),
    withLatestFrom(
      this.store$.pipe(select(selectExecutionHistoryParsingStatus), take(1)),
      this.store$.select(selectIsPathwaysSelectionActive)),
      tap(([action, status, isPathwaysSelectionActive]) =>
        this.sendLog('startLoadingPathwayEffect$', {
          actionType: action.type,
          status: status,
          isPathwaysSelectionActive: isPathwaysSelectionActive
        })),
      switchMap(([action, status, isPathwaysSelectionActive]) =>
        from([...this.getLoadingActions(status, isPathwaysSelectionActive), new TestBarEnableConfirmButtonAction(false)]))
    );

  @Effect()
  loadNotActivePathwayEffect$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateProgressionAction>(PathwayActionTypes.UPDATE_PATHWAY_TEST_PROGRESSION),
    withLatestFrom(this.store$.pipe(select(selectIsPathwaysSelectionActive))),
    tap(([action, isPathwaysSelectionActive]) =>
      this.sendLog('loadNotActivePathwayEffect$', {
        actionType: action.type,
        isPathwaysSelectionActive: isPathwaysSelectionActive
      })),
    filter(([action, isPathwaysSelectionActive]) => isPathwaysSelectionActive === false),
    switchMap(([action, isPathwaysSelectionActive]) => this.getActionsForPathwaySelectionNotActive(action.payload.progression))
  );

  @Effect()
  loadActivePathwayEffect$: Observable<Action> = this.actions$.pipe(
    ofType<HandlePathwaysDataFetchedByKeysAction>(LoadPathwaysActionTypes.HANDLE_FETCHED_BY_KEYS_ACTION),
    tap((action) => this.sendLog('loadActivePathwayEffect$', {actionType: action.type})),
    switchMap<any, any>((action) => {
      return Observable.zip(
        Observable.of(action),
        this.store$.pipe(select(selectPathwayTestProgression)).filter(progression => progression != null).pipe(take(1)));
    }),
    switchMap(([action, progression]) => this.getActionsForPathwaySelectionActive(action.payload.data, progression))
  );

  @Effect()
  updateBpmnNodesCurationDataEffect$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateBpmnNodesCurationData>(
      CurationInfoForPathwaysActionTypes.UPDATE_BPMN_NODES_CURATION_DATA
    ),
    tap((action) => this.sendLog('updateBpmnNodesCurationDataEffect$', {actionType: action.type})),
    map(action => {
      const nodesMap = {};
      Object.getOwnPropertyNames(action.payload.nodes).forEach((key) => this.getNodesDataByKey(action, key, 'guideBox', nodesMap));
      return nodesMap;
    }),
    map((nodes) =>
      new UpdateGuidedBoxesAction({guidedBoxNodeMap: nodes})
    ),
    catchError(error => of(new SetErrorAction({
      error,
      devMessage: 'updateBpmnNodesCurationDataEffect$: Failed to get data for guideboxes',
      userMessage: failedToLoadGuideboxesMessage
    })))
  );

  @Effect()
  updateBpmnNodesDependenciesCurationDataEffect$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateBpmnNodeSelectionDepdendenciesCurationData>(
      CurationInfoForPathwaysActionTypes.UPDATE_BPMN_NODE_SELECTION_DEPENDENCIES_CURATION_DATA
    ),
    tap((action) => this.sendLog('updateBpmnNodesDependenciesCurationDataEffect$', {actionType: action.type})),
    map((action) =>
      new UpdateNodesDependenciesAction({nodeSelectionDependencies: action.payload.nodeSelectionDependencies})
    ),
    catchError(error => of(new SetErrorAction({
      error,
      devMessage: 'updateBpmnNodesDependenciesCurationDataEffect$: Failed to get data for nodes dependencies',
      userMessage: failedToLoadNodesDependenciesMessage
    })))
  );

  @Effect()
  updateClinicalTrialsEffect$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateClinicalTrialsAction>(
      CurationInfoForPathwaysActionTypes.UPDATE_CLINICAL_TRIALS
    ),
    tap((action) => this.sendLog('updateClinicalTrialsEffect$', {actionType: action.type})),
    switchMap(action =>
      this.updateTreatmentNames(action.payload.nodeIdToClinicalTrials)
    )
  );

  @Effect()
  updateUserSelectionStatusEffect$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateProgressionAction>(PathwayActionTypes.UPDATE_PATHWAY_TEST_PROGRESSION),
    tap(() => this.sendLog('updateUserSelectionStatusEffect$', {})),
    switchMap(() => [new UpdateUserSelectionStatusAction({status: USER_SELECTION_STATUS.NotInProgress})])
  )

  constructor(private actions$: Actions,
              private patientDataStateStore$: Store<PatientDataState>,
              private injector: Injector,
              private store$: Store<any>,
              private logService: LogService) {

  }

  getActionsForPathwaySelectionActive(pathwaysExecutionData: PathwaysExecutionData,
                                      progression: Progression): Observable<Action> {
    let actions: Action[];
    actions = [
      new FetchClinicalTrialsAction({bpmnDeploymentIdentifier: pathwaysExecutionData.bpmnIdentifier}),
      ...(
        // GEN-31473: If pathway has been revised since progression was saved,
        // ignore the progression and restart with the revised pathway
        this.wasPathwayRevisedSinceProgression(pathwaysExecutionData, progression)
          ? [new InitExecutionAction({
            patientData: pathwaysExecutionData.patientData,
            bpmnDeplymentIdentifier: pathwaysExecutionData.bpmnIdentifier
          })]
          : this.loadExistingExecution(progression)
      ),
    ];
    return from(actions);
  }

  wasPathwayRevisedSinceProgression(pathwaysExecutionData: PathwaysExecutionData, progression: Progression): boolean {
    if (!progression || !progression.id) {
      return true;
    }
    if (!pathwaysExecutionData || !pathwaysExecutionData.curationItemId) {
      return false;
    }
    const progressionAdditionalProperties = _.get(progression, 'data.additionalProperties', null);
    return !(_.isEqual(pathwaysExecutionData.curationItemId, progressionAdditionalProperties.curationItemId));
  }

  getActionsForPathwaySelectionNotActive(progression): Action[] {
    return [new GetCurationItemById({
      curationItemId: _.get(progression, 'data.additionalProperties.curationItemId', null)
    }),
      ...this.loadExistingExecution(progression),
      new UpdateNodesNamesAction({
        nodeIdToNameSuffixDictionary: _.get(progression, 'data.additionalProperties.nodeIdsToNameSuffix', null)
      })];
  }

  loadExistingExecution(progression: Progression): Action[] {
    const bpmnDeploymentIdentifier = _.get(progression, 'data.additionalProperties.bpmnDeploymentIdentifier', null);
    const processInstanceId = _.get(progression, 'data.additionalProperties.processInstanceId', null);

    return [
      new LoadExsitingExecutionAction({
        bpmnDeploymentIdentifier: bpmnDeploymentIdentifier,
        processInstanceId: processInstanceId
      })
    ];
  }

  private updateTreatmentNames(nodeToClinicalTrialsMap): Observable<Action> {
    const nameSuffix = ' Clinical Trials';
    const nodeIdToNameSuffix = {};
    Object.keys(nodeToClinicalTrialsMap).forEach(key => nodeIdToNameSuffix[key] = `${nodeToClinicalTrialsMap[key].length}${nameSuffix}`);

    const actions = [
      new UpdateNodesNamesAction({nodeIdToNameSuffixDictionary: nodeIdToNameSuffix})
    ];
    return from(actions);
  }

  private getLoadingActions(status: BP_Status, isPathwaySelectionActive: boolean): Action[] {
    const actions =
      this.isAllDataAtStore(status)
        ? this.markPathwayDataWasPrefetched()
        : this.loadDataIsRequired(isPathwaySelectionActive);
    return actions;
  }

  private loadDataIsRequired(isPathwaySelectionActive: boolean): Action[] {
    return [
      new ConfigureProcessExecutorModuleAction(),
      new FetchPathwayProgressionAction(),
      ...(
        this.loadInCasePathwaySelectionActiveVsNot(isPathwaySelectionActive)
      )];
  }

  private loadInCasePathwaySelectionActiveVsNot(isPathwaySelectionActive: boolean): Action[] {
    return isPathwaySelectionActive
      ? [new SearchCurationItemByKeysAction()]
      : [];
  }

  private markPathwayDataWasPrefetched() {
    return [new PathwaysTriggerOff()];
  }

  private isAllDataAtStore(status: BP_Status) {
    return status === BP_Status.Completed;
  }

  private getPathwaysConfigurationService(): PathwaysConfigurationService {
    return this.injector.get(PathwaysConfigurationService);
  }

  private getNodesDataByKey(action, key: any, path: string, nodesMap: {}): void {
    const nodeData = _.get(action.payload, `nodes[${key}].data.${path}`, null);
    if (nodeData) {
      nodesMap[key] = nodeData;
    }
  }

  private sendLog(effect: string, paramsObj?: any) {
    let logMsg: string = '[LoadPathwaysEffects] ' + effect;
    Object.keys(paramsObj || {}).forEach((key) => logMsg += ' ' + key + '=' + paramsObj[key]);

    console.log(logMsg);
    this.logService.info(logMsg, '');
  }

}
